'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { Eye, EyeOff, Search, X } from 'lucide-react';
import { useDebounce } from '../../hooks/use-debounce';
import { cn } from '../../lib/utils';
type ValidationResult = string | null | undefined;
type ValidationMode = 'never' | 'blur' | 'change' | 'always';
type BaseFieldProps = {
  label?: React.ReactNode;
  helperText?: React.ReactNode;
  error?: React.ReactNode;
  validate?: (value: string) => ValidationResult;
  showValidation?: ValidationMode;
  containerClassName?: string;
  labelClassName?: string;
  helperClassName?: string;
  errorClassName?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  clearable?: boolean;
  showPasswordToggle?: boolean;
  debounceMs?: number;
  onDebouncedValueChange?: (value: string) => void;
};
export type InputFieldProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'prefix'> & BaseFieldProps;
function useFieldValidation(t0) {
  const $ = _c(5);
  const {
    value,
    error,
    validate,
    showValidation: t1
  } = t0;
  const showValidation = t1 === undefined ? "blur" : t1;
  const [touched, setTouched] = React.useState(false);
  let t2;
  bb0: {
    if (!validate || showValidation === "never") {
      t2 = null;
      break bb0;
    }
    if (showValidation === "blur" && !touched) {
      t2 = null;
      break bb0;
    }
    let t3;
    if ($[0] !== validate || $[1] !== value) {
      t3 = validate(value);
      $[0] = validate;
      $[1] = value;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    t2 = t3;
  }
  const validationMessage = t2;
  const t3 = error ?? validationMessage;
  let t4;
  if ($[3] !== t3) {
    t4 = {
      setTouched,
      message: t3
    };
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
export const InputField = React.forwardRef<HTMLInputElement, InputFieldProps>((t0, ref) => {
  const $ = _c(109);
  let ariaInvalid;
  let className;
  let containerClassName;
  let defaultValue;
  let disabled;
  let error;
  let errorClassName;
  let helperClassName;
  let helperText;
  let id;
  let label;
  let labelClassName;
  let leftIcon;
  let onBlur;
  let onChange;
  let onDebouncedValueChange;
  let props;
  let rightIcon;
  let showValidation;
  let t1;
  let t2;
  let t3;
  let t4;
  let validate;
  let value;
  if ($[0] !== t0) {
    ({
      className,
      containerClassName,
      labelClassName,
      helperClassName,
      errorClassName,
      label,
      helperText,
      error,
      validate,
      showValidation,
      leftIcon,
      rightIcon,
      clearable: t1,
      showPasswordToggle: t2,
      debounceMs: t3,
      onDebouncedValueChange,
      type: t4,
      value,
      defaultValue,
      onChange,
      onBlur,
      id,
      disabled,
      "aria-invalid": ariaInvalid,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = ariaInvalid;
    $[2] = className;
    $[3] = containerClassName;
    $[4] = defaultValue;
    $[5] = disabled;
    $[6] = error;
    $[7] = errorClassName;
    $[8] = helperClassName;
    $[9] = helperText;
    $[10] = id;
    $[11] = label;
    $[12] = labelClassName;
    $[13] = leftIcon;
    $[14] = onBlur;
    $[15] = onChange;
    $[16] = onDebouncedValueChange;
    $[17] = props;
    $[18] = rightIcon;
    $[19] = showValidation;
    $[20] = t1;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
    $[24] = validate;
    $[25] = value;
  } else {
    ariaInvalid = $[1];
    className = $[2];
    containerClassName = $[3];
    defaultValue = $[4];
    disabled = $[5];
    error = $[6];
    errorClassName = $[7];
    helperClassName = $[8];
    helperText = $[9];
    id = $[10];
    label = $[11];
    labelClassName = $[12];
    leftIcon = $[13];
    onBlur = $[14];
    onChange = $[15];
    onDebouncedValueChange = $[16];
    props = $[17];
    rightIcon = $[18];
    showValidation = $[19];
    t1 = $[20];
    t2 = $[21];
    t3 = $[22];
    t4 = $[23];
    validate = $[24];
    value = $[25];
  }
  const clearable = t1 === undefined ? false : t1;
  const showPasswordToggle = t2 === undefined ? false : t2;
  const debounceMs = t3 === undefined ? 250 : t3;
  const type = t4 === undefined ? "text" : t4;
  const generatedId = React.useId();
  const inputId = id ?? generatedId;
  const innerRef = React.useRef(null);
  const isControlled = value !== undefined;
  let t5;
  if ($[26] !== defaultValue) {
    t5 = defaultValue?.toString() ?? "";
    $[26] = defaultValue;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  const [uncontrolledValue, setUncontrolledValue] = React.useState(t5);
  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);
  let t6;
  if ($[28] !== isControlled || $[29] !== uncontrolledValue || $[30] !== value) {
    t6 = isControlled ? value?.toString() ?? "" : uncontrolledValue;
    $[28] = isControlled;
    $[29] = uncontrolledValue;
    $[30] = value;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  let t7;
  if ($[32] !== t6) {
    t7 = t6.toString();
    $[32] = t6;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  const currentValue = t7;
  const debouncedValue = useDebounce(currentValue, debounceMs);
  let t8;
  if ($[34] !== currentValue || $[35] !== error || $[36] !== showValidation || $[37] !== validate) {
    t8 = {
      value: currentValue,
      error,
      validate,
      showValidation
    };
    $[34] = currentValue;
    $[35] = error;
    $[36] = showValidation;
    $[37] = validate;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  const validation = useFieldValidation(t8);
  const isInvalid = Boolean(validation.message) || Boolean(ariaInvalid);
  const isChoice = type === "checkbox" || type === "radio";
  const canTogglePassword = type === "password" && showPasswordToggle;
  let t9;
  if ($[39] !== label) {
    t9 = typeof label === "string" ? label.toLowerCase() : "password";
    $[39] = label;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  const passwordFieldName = t9;
  let t10;
  let t11;
  if ($[41] !== debouncedValue || $[42] !== onDebouncedValueChange) {
    t10 = () => {
      onDebouncedValueChange?.(debouncedValue);
    };
    t11 = [debouncedValue, onDebouncedValueChange];
    $[41] = debouncedValue;
    $[42] = onDebouncedValueChange;
    $[43] = t10;
    $[44] = t11;
  } else {
    t10 = $[43];
    t11 = $[44];
  }
  React.useEffect(t10, t11);
  let t12;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => innerRef.current as HTMLInputElement;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  React.useImperativeHandle(ref, t12);
  let t13;
  if ($[46] !== isControlled || $[47] !== onChange) {
    t13 = event => {
      if (!isControlled) {
        setUncontrolledValue(event.target.value);
      }
      onChange?.(event);
    };
    $[46] = isControlled;
    $[47] = onChange;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  const handleChange = t13;
  let t14;
  if ($[49] !== onBlur || $[50] !== validation) {
    t14 = event_0 => {
      validation.setTouched(true);
      onBlur?.(event_0);
    };
    $[49] = onBlur;
    $[50] = validation;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  const handleBlur = t14;
  let t15;
  if ($[52] !== isControlled) {
    t15 = () => {
      if (!innerRef.current) {
        return;
      }
      const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
      setter?.call(innerRef.current, "");
      innerRef.current.dispatchEvent(new Event("input", {
        bubbles: true
      }));
      if (!isControlled) {
        setUncontrolledValue("");
      }
    };
    $[52] = isControlled;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  const clearValue = t15;
  const t16 = canTogglePassword && isPasswordVisible ? "text" : type;
  const t17 = isInvalid || undefined;
  const t18 = helperText || validation.message ? `${inputId}-message` : undefined;
  const t19 = isChoice ? "disabled:cursor-not-allowed disabled:opacity-50" : "text-foreground file:text-foreground placeholder:text-muted-foreground/55 selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base leading-[1.75] shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:!outline-none aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive";
  const t20 = leftIcon && !isChoice && "pl-10";
  const t21 = (rightIcon || canTogglePassword || clearable && currentValue) && !isChoice && "pr-10";
  let t22;
  if ($[54] !== className || $[55] !== t19 || $[56] !== t20 || $[57] !== t21) {
    t22 = cn(t19, t20, t21, className);
    $[54] = className;
    $[55] = t19;
    $[56] = t20;
    $[57] = t21;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] !== defaultValue || $[60] !== disabled || $[61] !== handleBlur || $[62] !== handleChange || $[63] !== inputId || $[64] !== props || $[65] !== t16 || $[66] !== t17 || $[67] !== t18 || $[68] !== t22 || $[69] !== value) {
    t23 = <input ref={innerRef} id={inputId} type={t16} value={value} defaultValue={defaultValue} disabled={disabled} aria-invalid={t17} aria-describedby={t18} onChange={handleChange} onBlur={handleBlur} data-slot="input" className={t22} {...props} />;
    $[59] = defaultValue;
    $[60] = disabled;
    $[61] = handleBlur;
    $[62] = handleChange;
    $[63] = inputId;
    $[64] = props;
    $[65] = t16;
    $[66] = t17;
    $[67] = t18;
    $[68] = t22;
    $[69] = value;
    $[70] = t23;
  } else {
    t23 = $[70];
  }
  const input = t23;
  if (!label && !helperText && !validation.message && !leftIcon && !rightIcon && !clearable && !canTogglePassword) {
    return input;
  }
  let t24;
  if ($[71] !== containerClassName) {
    t24 = cn("grid gap-1.5", containerClassName);
    $[71] = containerClassName;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  let t25;
  if ($[73] !== inputId || $[74] !== label || $[75] !== labelClassName) {
    t25 = label ? <label htmlFor={inputId} className={cn("text-sm font-medium text-muted-foreground", labelClassName)}>{label}</label> : null;
    $[73] = inputId;
    $[74] = label;
    $[75] = labelClassName;
    $[76] = t25;
  } else {
    t25 = $[76];
  }
  const t26 = isChoice && "flex items-center gap-2";
  let t27;
  if ($[77] !== t26) {
    t27 = cn("relative", t26);
    $[77] = t26;
    $[78] = t27;
  } else {
    t27 = $[78];
  }
  let t28;
  if ($[79] !== isChoice || $[80] !== leftIcon) {
    t28 = leftIcon && !isChoice ? <span className="pointer-events-none absolute left-3 top-1/2 flex size-4 -translate-y-1/2 items-center justify-center text-muted-foreground">{leftIcon}</span> : null;
    $[79] = isChoice;
    $[80] = leftIcon;
    $[81] = t28;
  } else {
    t28 = $[81];
  }
  let t29;
  if ($[82] !== canTogglePassword || $[83] !== clearValue || $[84] !== clearable || $[85] !== currentValue || $[86] !== disabled || $[87] !== inputId || $[88] !== isChoice || $[89] !== isPasswordVisible || $[90] !== passwordFieldName || $[91] !== rightIcon) {
    t29 = canTogglePassword ? <button type="button" disabled={disabled} aria-controls={inputId} aria-label={`${isPasswordVisible ? "Hide" : "Show"} ${passwordFieldName}`} aria-pressed={isPasswordVisible} onClick={() => setIsPasswordVisible(_temp)} className="absolute right-2 top-1/2 flex size-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 text-center leading-[normal]">{isPasswordVisible ? <EyeOff className="size-4" aria-hidden="true" /> : <Eye className="size-4" aria-hidden="true" />}</button> : !isChoice && clearable && currentValue ? <button type="button" onClick={clearValue} className="absolute right-2 top-1/2 flex size-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 text-center leading-[normal]" aria-label="Clear input"><X className="size-4" /></button> : rightIcon && !isChoice ? <span className="pointer-events-none absolute right-3 top-1/2 flex size-4 -translate-y-1/2 items-center justify-center text-muted-foreground">{rightIcon}</span> : null;
    $[82] = canTogglePassword;
    $[83] = clearValue;
    $[84] = clearable;
    $[85] = currentValue;
    $[86] = disabled;
    $[87] = inputId;
    $[88] = isChoice;
    $[89] = isPasswordVisible;
    $[90] = passwordFieldName;
    $[91] = rightIcon;
    $[92] = t29;
  } else {
    t29 = $[92];
  }
  let t30;
  if ($[93] !== input || $[94] !== t27 || $[95] !== t28 || $[96] !== t29) {
    t30 = <div className={t27}>{t28}{input}{t29}</div>;
    $[93] = input;
    $[94] = t27;
    $[95] = t28;
    $[96] = t29;
    $[97] = t30;
  } else {
    t30 = $[97];
  }
  let t31;
  if ($[98] !== errorClassName || $[99] !== helperClassName || $[100] !== helperText || $[101] !== inputId || $[102] !== validation.message) {
    t31 = helperText || validation.message ? <p id={`${inputId}-message`} className={cn("text-xs", validation.message ? "text-destructive" : "text-muted-foreground", validation.message ? errorClassName : helperClassName)}>{validation.message ?? helperText}</p> : null;
    $[98] = errorClassName;
    $[99] = helperClassName;
    $[100] = helperText;
    $[101] = inputId;
    $[102] = validation.message;
    $[103] = t31;
  } else {
    t31 = $[103];
  }
  let t32;
  if ($[104] !== t24 || $[105] !== t25 || $[106] !== t30 || $[107] !== t31) {
    t32 = <div className={t24}>{t25}{t30}{t31}</div>;
    $[104] = t24;
    $[105] = t25;
    $[106] = t30;
    $[107] = t31;
    $[108] = t32;
  } else {
    t32 = $[108];
  }
  return t32;
});
InputField.displayName = 'InputField';
export type SearchFieldProps = Omit<InputFieldProps, 'type' | 'leftIcon'>;
export const SearchField = React.forwardRef<HTMLInputElement, SearchFieldProps>((props, ref) => {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Search className="size-4" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props || $[2] !== ref) {
    t1 = <InputField ref={ref} type="text" leftIcon={t0} {...props} />;
    $[1] = props;
    $[2] = ref;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
});
SearchField.displayName = 'SearchField';
function _temp(isVisible) {
  return !isVisible;
}